import type { EvaluationPlanAttachment } from '../types/evaluationPlan';
import {
  parseEvaluationPlanSheetCanvasDocument,
  type EvaluationPlanSheetCanvasDocumentV1,
} from '../types/evaluationPlanSheetCanvas';

export function normalizeShareLinkUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function guessFileNameFromShareUrl(shareUrl: string): string | undefined {
  try {
    const url = new URL(shareUrl);
    const last = url.pathname.split('/').filter(Boolean).pop();
    if (last && last.includes('.')) return decodeURIComponent(last);
  } catch {
    /* ignore */
  }
  return undefined;
}

export function extractGoogleDriveFileId(shareUrl: string): string | null {
  try {
    const url = new URL(shareUrl);
    const host = url.hostname.replace(/^www\./, '');
    if (host !== 'drive.google.com') return null;
    const parts = url.pathname.split('/').filter(Boolean);
    if (parts[0] === 'file' && parts[1] === 'd' && parts[2]) return parts[2];
    return url.searchParams.get('id');
  } catch {
    return null;
  }
}

export function resolveShareLinkDownloadUrl(shareUrl: string): string {
  const fileId = extractGoogleDriveFileId(shareUrl);
  if (fileId) return `https://drive.google.com/uc?export=download&id=${fileId}`;
  return shareUrl;
}

export function isShareLinkAttachment(
  attachment: EvaluationPlanAttachment | null | undefined
): attachment is EvaluationPlanAttachment & { kind: 'share_link'; share_url: string } {
  return attachment?.kind === 'share_link' && !!attachment.share_url?.trim();
}

export function isWebDocumentAttachment(
  attachment: EvaluationPlanAttachment | null | undefined
): attachment is EvaluationPlanAttachment & { kind: 'web_document' } {
  return attachment?.kind === 'web_document';
}

export function isSheetCanvasAttachment(
  attachment: EvaluationPlanAttachment | null | undefined
): attachment is EvaluationPlanAttachment & { kind: 'web_document'; slot: 'sheet' } {
  if (!isWebDocumentAttachment(attachment)) return false;
  return getSheetCanvasFromAttachment(attachment) != null;
}

export function getSheetCanvasFromAttachment(
  attachment: EvaluationPlanAttachment | null | undefined
): EvaluationPlanSheetCanvasDocumentV1 | null {
  if (!attachment?.form_data || typeof attachment.form_data !== 'object') return null;
  const raw = (attachment.form_data as Record<string, unknown>).sheet_canvas;
  return parseEvaluationPlanSheetCanvasDocument(raw);
}

export function isCriterionTableWebDocumentAttachment(
  attachment: EvaluationPlanAttachment | null | undefined
): boolean {
  if (!isWebDocumentAttachment(attachment)) return false;
  if (attachment.slot === 'criterion') return true;
  if (getSheetCanvasFromAttachment(attachment)) return false;
  const raw = attachment.form_data?.web_document;
  return raw != null;
}

export function attachmentSupportsShareLinkActions(
  attachment: EvaluationPlanAttachment | null | undefined
): attachment is EvaluationPlanAttachment & { share_url: string } {
  return !!attachment?.share_url?.trim();
}

function triggerDownloadUrl(downloadUrl: string, fileName?: string): void {
  const isGoogleDrive = /drive\.google\.com/i.test(downloadUrl);

  if (isGoogleDrive) {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'display:none;position:fixed;width:0;height:0;border:0';
    iframe.src = downloadUrl;
    document.body.appendChild(iframe);
    window.setTimeout(() => iframe.remove(), 60_000);
    return;
  }

  const anchor = document.createElement('a');
  anchor.href = downloadUrl;
  anchor.style.display = 'none';
  if (fileName) anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

export function triggerEvaluationPlanShareLinkDownload(attachment: EvaluationPlanAttachment): void {
  const shareUrl = attachment.share_url?.trim();
  if (!shareUrl) return;

  const downloadUrl = resolveShareLinkDownloadUrl(shareUrl);
  const fileName = attachment.file_name?.trim();
  triggerDownloadUrl(downloadUrl, fileName);
}

export function openEvaluationPlanAttachment(attachment: EvaluationPlanAttachment | null | undefined): void {
  if (!attachment) return;
  if (isShareLinkAttachment(attachment)) {
    triggerEvaluationPlanShareLinkDownload(attachment);
    return;
  }
  const url = attachment.storage_path?.trim();
  if (url && /^https?:\/\//i.test(url)) {
    triggerEvaluationPlanShareLinkDownload({ ...attachment, share_url: url, kind: 'share_link' });
  }
}
